import React from 'react';
import {
  Plugins,
  withSystem,
  Atom,
  Expressions,
  type AtomRenderProps,
} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {SHARED_FORM_CONSTRAINTS_DESCENDANTS} from '@packages/systems/core/constants/SharedConstraintRules';
import {SHARED_INPUT_WRAPPER_CONSTRAINTS_DESCENDANTS} from '@packages/shared/constants/inputWrapperConstraints';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {
  TRecord,
  TTypeConstructor,
  TText,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  findChild,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';
import {
  isFormCheckboxInput,
  isUserFormCheckboxInput,
} from '@packages/utilities/elementTypeUtils';
import {CHECKBOX_DEFAULT} from './constants';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';

const clearfixBefore = {
  content: `' '`,
  display: 'table',
  gridColumnEnd: 2,
  gridColumnStart: 1,
  gridRowEnd: 2,
  gridRowStart: 1,
} as const;

const clearfixAfter = {...clearfixBefore, clear: 'both'} as const;

const isCheckboxInputChild = (element: ExpressionElement) =>
  isFormCheckboxInput(element) || isUserFormCheckboxInput(element);

const FormCheckboxWrapper = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    data,
    context,
    renderChild,
  }: AtomRenderProps) => {
    const checkboxInputChild = findChild(data, isCheckboxInputChild);
    const checkboxInputElement = getInPojo(checkboxInputChild, [
      'props',
      'element',
    ]) as ExpressionElement | undefined;
    const name = checkboxInputElement
      ? Expressions.getElementDataAt(
          checkboxInputElement,
          [
            {in: 'Record', at: 'attr'},
            {in: 'Record', at: 'name'},
          ],
          'Text',
          CHECKBOX_DEFAULT
        )
      : CHECKBOX_DEFAULT;
    const updatedContext = context.set('for', name);

    return (
      // TODO: fix jsx-a11y lint error
      // eslint-disable-next-line jsx-a11y/label-has-for
      <label {...generateAttributes(attributes, classes)}>
        {getChildren(data).map((child) => renderChild(child, updatedContext))}
      </label>
    );
  }
);

const config = createDevlinkAtom({
  className: 'w-checkbox',
  constraints: {
    ancestors: [
      {
        is: ['FormForm', 'UserSignUpForm', 'UserAccountForm'],
        name: 'Form',
        rule: 'ExactlyOne',
      },
    ],
    descendants: [
      ...SHARED_FORM_CONSTRAINTS_DESCENDANTS,
      ...SHARED_INPUT_WRAPPER_CONSTRAINTS_DESCENDANTS,
    ],
  },
  displayName: 'Checkbox Field',
  render: FormCheckboxWrapper,
  showEmpty: true,
  fields: {
    form: withSystem(
      {private: true, defaultValue: CHECKBOX_DEFAULT},
      TRecord({type: TText})
    ),
    children: withSystem(
      {
        labelVariant: 'hidden',
      },
      TTypeConstructor(
        qualify(Plugins.name('Form'), typeName('CheckboxChildren'))
      )
    ),
  },
  style: {
    base: {
      main: {
        noPseudo: {
          display: 'block',
          marginBottom: px(5),
          paddingLeft: px(20),
        },
        before: {
          ...clearfixBefore,
        },
        after: {
          ...clearfixAfter,
        },
      },
    },
  },
  showSettingsInDescendants: true,
  // Add the native ID path in attributes for Analyze tracking
  autoEngagementTrackingAttribute: true,
});

export default config;
